import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule, FormGroup, FormBuilder, Validators } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatNativeDateModule } from '@angular/material/core';
import { ToastrService } from 'ngx-toastr';
import { SYSTEM_MAINTAINANCE_TAB_OPTIONS } from 'src/app/lib/constants/tabs.constants';
import { ApiService } from 'src/app/services/api.service';
import { RedeemService } from './redeem.service';
import { SharedComponentsModule } from 'src/app/shared/modules/shared-component.module';
import { PopUpDialogService } from 'src/app/services/popup-dialog.service';
import { DateInputComponent } from 'src/app/shared/component/date-input/date-input.component';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { MegapayLoaderComponent } from 'src/app/shared/component/megapay-loader/megapay-loader.component';
import { SharedDirectivesModule } from 'src/app/shared/modules/shared-directives.module';

// ANIMATIONS
import { trigger, style, animate, transition } from '@angular/animations';

interface PreSetData {
  rate: number | null;
  date: string | Date | null;
  isSet: boolean;
}

@Component({
  selector: 'app-redeem',
  templateUrl: './redeem.component.html',
  standalone: true,
  imports: [
    CommonModule,
    SharedComponentsModule,
    SharedDirectivesModule,
    FormsModule,
    ReactiveFormsModule,
    MatDatepickerModule,
    MatInputModule,
    MatFormFieldModule,
    MatNativeDateModule,
    DateInputComponent,
    MegapayLoaderComponent,
    NgxSpinnerModule,
    SharedDirectivesModule
  ],
  styleUrl: './redeem.component.scss',
  animations: [
    trigger('popupAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-10px) scale(0.95)' }),
        animate('200ms ease-out', style({ opacity: 1, transform: 'translateY(0) scale(1)' }))
      ]),
      transition(':leave', [
        animate('150ms ease-in', style({ opacity: 0, transform: 'translateY(-10px) scale(0.95)' }))
      ])
    ])
  ]
})
export class RedeemComponent implements OnInit {
  tabs = SYSTEM_MAINTAINANCE_TAB_OPTIONS;
  isLoading = false;

  // restrict past dates 
  minDate: Date = new Date(new Date().setDate(new Date().getDate() + 1));

  redeemPercentageBuyload = 0;
  redeemPercentagePins = 0;
  redeemPercentageStores = 0;
  redeemPercentagePaybills = 0;

  isEditingBuyload = false;
  isEditingPins = false;
  isEditingStores = false;
  isEditingPaybills = false;

  originalRates: { [key: string]: number } = {};

  activeCategory: 'buyload' | 'pins' | 'stores' | 'paybills' | null = null;
  
  presetForm: FormGroup;

  preSetRates: { [key: string]: PreSetData } = {
    buyload: { rate: null, date: null, isSet: false },
    pins: { rate: null, date: null, isSet: false },
    stores: { rate: null, date: null, isSet: false },
    paybills: { rate: null, date: null, isSet: false }
  };

  constructor(
    public Toastr: ToastrService,
    private apiService: ApiService,
    private redeemService: RedeemService,
    public popUpDialog: PopUpDialogService,
    private fb: FormBuilder,
    private spinner: NgxSpinnerService,
  ) {

    // Initialize the temporary form for the popup
    this.presetForm = this.fb.group({
      rate: [null],
      date: [null]
    });
  }

  ngOnInit() {
    this.get_redeem_percentage().catch(console.error);
  }

  async get_redeem_percentage() {
    try {
      this.spinner.show()
      this.isLoading = true;
      
      this.apiService.getRedeemPercentage().subscribe({
        next: (response) => {
          const mappedData = this.redeemService.mapApiResultToState(response.result as any);
          this.redeemPercentageBuyload = mappedData.buyload;
          this.redeemPercentagePins = mappedData.pins;
          this.redeemPercentageStores = mappedData.stores;
          this.redeemPercentagePaybills = mappedData.paybills;
          
          this.preSetRates = mappedData.preSetRates as any;
        },
        error: (err) => {
          this.Toastr.error('Failed to load redeem percentages');
        },
        complete: () => {
          this.isLoading = false;
          this.spinner.hide();
        }
      });
    } catch (error) {
      this.Toastr.error('Failed to load redeem percentages');
      this.isLoading = false;
    }
  }

  toggleEdit(field: 'buyload' | 'pins' | 'stores' | 'paybills') {
    let isEditing = false;
    let currentRate = 0;

    switch(field) {
      case 'buyload': isEditing = this.isEditingBuyload; currentRate = this.redeemPercentageBuyload; break;
      case 'pins': isEditing = this.isEditingPins; currentRate = this.redeemPercentagePins; break;
      case 'stores': isEditing = this.isEditingStores; currentRate = this.redeemPercentageStores; break;
      case 'paybills': isEditing = this.isEditingPaybills; currentRate = this.redeemPercentagePaybills; break;
    }

    if (!isEditing) {
      this.originalRates[field] = currentRate;
      this.setEditState(field, true);
    } else {
      if (this.originalRates[field] !== undefined && this.originalRates[field] !== currentRate) {
        this.popUpDialog.openSaveActionDialog(
          'You have unsaved changes. Are you sure you want to discard them?',
          () => {
            this.revertRate(field);
            this.setEditState(field, false);
            this.Toastr.success('Changes discarded');
          },
          () => { }
        );
      } else {
        this.setEditState(field, false);
      }
    }
  }

  setEditState(field: string, state: boolean) {
    switch(field) {
      case 'buyload': this.isEditingBuyload = state; break;
      case 'pins': this.isEditingPins = state; break;
      case 'stores': this.isEditingStores = state; break;
      case 'paybills': this.isEditingPaybills = state; break;
    }
  }

  revertRate(field: string) {
    const original = this.originalRates[field];
    if (original !== undefined) {
      switch(field) {
        case 'buyload': this.redeemPercentageBuyload = original; break;
        case 'pins': this.redeemPercentagePins = original; break;
        case 'stores': this.redeemPercentageStores = original; break;
        case 'paybills': this.redeemPercentagePaybills = original; break;
      }
    }
  }

  async update_redeem_percentage(field: 'buyload' | 'pins' | 'stores' | 'paybills') {
    try {
      let value = 0;
      switch (field) {
        case 'buyload': value = this.redeemPercentageBuyload; break;
        case 'pins': value = this.redeemPercentagePins; break;
        case 'stores': value = this.redeemPercentageStores; break;
        case 'paybills': value = this.redeemPercentagePaybills; break;
      }

      if (value === null || value === undefined || value.toString() === '') {
        this.Toastr.error('Please enter a valid rate. The field cannot be empty.');
        return;
      }

      if (value < 0 || value > 100) {
        this.Toastr.error('Percentage must be between 0 and 100');
        return;
      }

      this.popUpDialog.openSaveActionDialog(
      `Are you sure you want to save?`,
      async () => {
        const payload = this.redeemService.createUpdatePayload(field, value);
  
        this.apiService.saveRedeemPercentage(payload).subscribe({
          next: (response) => {
            this.Toastr.success(`${field.charAt(0).toUpperCase() + field.slice(1)} Active Rate Has Been Successfully Updated`);
            
            // This ensures future "Cancel" clicks revert to this new value.
            this.originalRates[field] = value; 

            // Lock the input field (Turn off edit mode)
            this.setEditState(field, false); 

          },
          error: () => {
            this.Toastr.error('Failed to update redeem percentage');
          }
        });
      },
      () => {
        console.log('Save cancelled by user');
      });
    } catch (error) {
      this.Toastr.error('Failed to update redeem percentage');
    }
  }

  // --- PRE-SET RATES LOGIC ---

  handlePreSetClick(category: 'buyload' | 'pins' | 'stores' | 'paybills') {
    if (this.activeCategory === category) {
      this.closePopup();
    } else {
      this.activeCategory = category;
      const existingData = this.preSetRates[category];
      
      // Load data into Form instead of temp variables
      if (existingData.isSet) {
        this.presetForm.setValue({
          rate: existingData.rate,
          date: existingData.date ? new Date(existingData.date) : null
        });
      } else {
        this.presetForm.reset();
      }
    }
  }

  closePopup() {
    if (!this.activeCategory) return;

    const formValue = this.presetForm.value;
    const originalData = this.preSetRates[this.activeCategory];

    // Check if there are unsaved changes
    const hasChanges = this.redeemService.hasUnsavedPresetChanges(formValue, originalData);

    if (hasChanges) {
      this.popUpDialog.openSaveActionDialog(
        'You have unsaved changes. Are you sure you want to discard them?',
        () => {
          // User confirmed - close the popup
          this.activeCategory = null;
          this.presetForm.reset();
          this.Toastr.success('Changes discarded');
        },
        () => {
          // User cancelled - keep popup open
          console.log('Cancel action cancelled by user');
        }
      );
    } else {
      // No changes, close directly
      this.activeCategory = null;
      this.presetForm.reset();
    }
  }

  savePreSet() {
    if (!this.activeCategory) return;
    
    // Get values from Form
    const { rate, date } = this.presetForm.value;

    if (rate === null || rate === undefined || !date) {
      this.Toastr.error('Please Enter A Valid Redeem Rate and Date. The field cannot be empty.');
      return;
    }
    if (rate < 0 || rate > 100) {
      this.Toastr.error('Rate must be between 0 and 100');
      return;
    }
    
    const categoryTarget = this.activeCategory.charAt(0).toUpperCase() + this.activeCategory.slice(1);
    this.popUpDialog.openSaveActionDialog(
      `Are you sure you want to pre set this for ${categoryTarget}?`,
      async () => {
        const payload = this.redeemService.createPreSetPayload(
          this.activeCategory!, 
          rate, 
          date
        );
    
        this.isLoading = true;
    
        this.apiService.saveRedeemPreSet(payload).subscribe({
          next: (response) => {
            if (this.activeCategory) {
              this.preSetRates[this.activeCategory] = {
                rate: rate,
                date: this.redeemService.formatDateForApi(date),
                isSet: true
              };
              this.Toastr.success(`${this.activeCategory.charAt(0).toUpperCase() + this.activeCategory.slice(1)} Pre - Set Rate Has Been Successfully added.`);
            }
            this.isLoading = false;
            this.closePopup();
          },
          error: () => {
            this.Toastr.error('Failed to save pre-set rate.');
            this.isLoading = false;
          }
        });
      },
      () => {
        console.log('Save cancelled by user');
      }
    );
  }

  clearPreSet(category: 'buyload' | 'pins' | 'stores' | 'paybills') {
    const categoryTarget = category.toUpperCase();
    const categoryDisplay = category.charAt(0).toUpperCase() + category.slice(1);
    
    this.popUpDialog.openSaveActionDialog(
      `Are you sure you want to clear the pre-set for ${categoryDisplay}?`,
      async () => {
        const payload = this.redeemService.createClearPreSetPayload(category);
        
        this.isLoading = true;
        
        this.apiService.saveRedeemPreSet(payload).subscribe({
          next: () => {
            this.preSetRates[category] = {
              rate: null,
              date: null,
              isSet: false
            };
            this.Toastr.success(`${categoryDisplay} Pre - Set Rate Has Been Successfully Cleared.`);
            this.isLoading = false;
            this.get_redeem_percentage().catch(console.error);
          },
          error: () => {
            this.Toastr.error('Failed to clear pre-set rate.');
            this.isLoading = false;
          }
        });
      },
      () => {
        console.log('Clear cancelled by user');
      }
    );
  }

  getDisplayDate(dateValue: string | Date | null): string {
    return this.redeemService.formatToLongDate(dateValue);
  }
}